import find from 'lodash/find';
import omit from 'lodash/omit';
import { Packages } from '../Constants';

const splitDelimiter = ',';
const joinDelimiter = ', ';

const valuesAsArray = valuesAsString =>
  (valuesAsString || '')
    .split(splitDelimiter)
    .map(x => x.trim())
    .filter(x => x !== '');

const standsAsArray = standsAsString =>
  (standsAsString || '')
    .split(splitDelimiter)
    .map(name => ({ name: name.trim() }))
    .filter(stand => stand.name !== '');

const filterSocialMediaUrls = socialMediaUrls => (socialMediaUrls || []).filter(s => s.url !== '');

const exOrgCreateMutationModelFromProfile = profile => ({
  companyName: profile.companyName.trim(),
  displayName: profile.name.trim(),
  addressLine1: profile.address.addressLine1,
  addressLine2: profile.address.addressLine2,
  city: profile.address.city,
  stateProvince: profile.address.stateProvince,
  postcode: profile.address.postcode,
  countryCode: profile.address.countryCode,
  package: Object.keys(Packages).find(key => Packages[key] === profile.packageId).toUpperCase(),
  stands: standsAsArray(profile.standReference),
  isNew: profile.isNew,
  extraCharacteristics: profile.extraCharacteristics,
  administratorEmail: profile.primaryAdministratorEmail,
  administratorFirstName: profile.primaryAdministratorFirstName,
  administratorLastName: profile.primaryAdministratorLastName
});

const addExhibitingOrganisationMutationModel = profile => ({
  eventEditionId: profile.eventEditionId,
  exhibitingOrganisation: exOrgCreateMutationModelFromProfile(profile)
});
const addMainStandHolderMutationModel = profile => ({
  eventEditionId: profile.eventEditionId,
  exhibitingOrganisation: exOrgCreateMutationModelFromProfile(profile)
});
const privateRequestModel = (profile, locale, isCreating) => ({
  website: profile.website,
  accompanyingWebsiteUrl: profile.accompanyingWebsiteUrl,
  phone: profile.phone,
  email: profile.email,
  packageId: profile.packageId,
  exhibitorTypeId: profile.exhibitorTypeId,
  hideAddress: profile.hideAddress,
  companyName: profile.companyName.trim(),
  companyNameAide: profile.companyNameAide ? profile.companyNameAide.trim() : null,
  primaryAdministratorEmail: profile.primaryAdministratorEmail,
  stands: standsAsArray(profile.standReference),
  filterItems: [],
  productsAndServices: (profile.productsAndServices || []).map(f => f.id),
  isNew: profile.isNew,
  extraCharacteristics: profile.extraCharacteristics,
  isPublished: profile.isPublished,
  socialMedia: filterSocialMediaUrls(profile.socialMedia),
  profileQuestions: profile.profileQuestions,
  filterCategories: profile.filterCategories,
  companyObjectives: profile.companyObjectives,
  sponsoredCategory: profile.sponsoredCategory,
  multilingual: [
    {
      locale,
      name: profile.name.trim() ?? profile.companyName,
      description: profile.description,
      showObjective: profile.showObjective,
      representedBrands: isCreating ? null : valuesAsArray(profile.representedBrandsAsString),
      sortAlias: profile.sortAlias,
      address: profile.address
    }
  ]
});

const protectedRequestModel = (profile, locale) => {
  const requestObject = {
    website: profile.website,
    accompanyingWebsiteUrl: profile.accompanyingWebsiteUrl,
    phone: profile.phone,
    email: profile.email,
    exhibitorTypeId: profile.exhibitorTypeId,
    hideAddress: profile.hideAddress,
    filterItems: [],
    socialMedia: filterSocialMediaUrls(profile.socialMedia),
    productsAndServices: (profile.productsAndServices || []).map(f => f.id),
    profileQuestions: profile.profileQuestions,
    filterCategories: profile.filterCategories,
    companyObjectives: profile.companyObjectives,
    sponsoredCategory: profile.sponsoredCategory,
    multilingual: [
      {
        locale,
        name: profile.name ?? profile.companyName,
        description: profile.description,
        showObjective: profile.showObjective,
        representedBrands: valuesAsArray(profile.representedBrandsAsString),
        address: profile.address
      }
    ]
  };
  return { ...requestObject, stands: standsAsArray(profile.standReference) };
};

const sharerRequestModel = (sharer, parentId) => ({
  mainStandHolderId: parentId,
  sharer: {
    companyName: sharer.companyName,
    displayName: sharer.name,
    administratorEmail: sharer.primaryAdministratorEmail,
    administratorFirstName: sharer.primaryAdministratorFirstName,
    administratorLastName: sharer.primaryAdministratorLastName,
    addressLine1: sharer.address.addressLine1,
    addressLine2: sharer.address.addressLine2,
    city: sharer.address.city,
    stateProvince: sharer.address.stateProvince,
    postcode: sharer.address.postcode,
    countryCode: sharer.address.countryCode,
    isNew: sharer.isNew,
    extraCharacteristics: sharer.extraCharacteristics,
    stands: sharer.stands
  }
});

const getFilterItemName = (filterItems, filterItemId) => {
  const filterItem = find(filterItems, fi => fi.id === filterItemId);
  return filterItem ? filterItem.name : null;
};

const getFilterItemsForDetails = (filterItemIds, filterItems) =>
  filterItemIds.map(item => ({
    id: item.id,
    name: getFilterItemName(filterItems, item.id)
  }));

const getFilterItems = (filterItemIds, filterItems) =>
    filterItemIds.map(filterItemId => ({
      id: filterItemId,
      name: getFilterItemName(filterItems, filterItemId)
    }));

const constructMultilingual = (profile, dataLocaleMultilingual, primaryLocaleMultilingual) => {
  const multilingual = Object.assign({}, dataLocaleMultilingual, { address: (dataLocaleMultilingual && dataLocaleMultilingual.address) || {} });
  if (primaryLocaleMultilingual != null) {
    if (!multilingual.address || !multilingual.address.addressLine1) {
      const customAddress = {
        addressLine1: primaryLocaleMultilingual.address.addressLine1,
        countryCode: primaryLocaleMultilingual.address.countryCode
      };
      multilingual.address = customAddress;
    }
    multilingual.name = multilingual.name || primaryLocaleMultilingual.name;
    multilingual.sortAlias = multilingual.sortAlias || primaryLocaleMultilingual.sortAlias;
  }
  multilingual.coverImage = dataLocaleMultilingual ? dataLocaleMultilingual.coverImage : null;
  multilingual.logo = dataLocaleMultilingual ? dataLocaleMultilingual.logo : null;
  multilingual.stands = profile.stands.map(stand => stand.name).join(joinDelimiter);
  multilingual.representedBrandsAsString = dataLocaleMultilingual ? dataLocaleMultilingual.representedBrands.join(joinDelimiter) : '';
  return multilingual;
};

const getRepresentedBrandsForDetails = (dataLocaleMultilingual, primaryLocaleMultilingual) => {
  const representedBrandsMultilingual = dataLocaleMultilingual ? dataLocaleMultilingual.representedBrands : [];
  if (representedBrandsMultilingual === null || representedBrandsMultilingual.length === 0) {
    return primaryLocaleMultilingual ? primaryLocaleMultilingual.representedBrands : [];
  }
  return representedBrandsMultilingual;
};

const constructMultilingualForDetails = (profile, dataLocaleMultilingual, primaryLocaleMultilingual) => {
  const multilingual = Object.assign({}, { address: {} });

  if (dataLocaleMultilingual?.addressLine1){
    multilingual.address.addressLine1 = dataLocaleMultilingual.addressLine1
    multilingual.address.addressLine2 = dataLocaleMultilingual.addressLine2;
    multilingual.address.city = dataLocaleMultilingual.city;
    multilingual.address.stateProvince = dataLocaleMultilingual.stateProvince;
    multilingual.address.postcode = dataLocaleMultilingual.postcode;
    multilingual.address.countryCode = dataLocaleMultilingual.countryCode;
    multilingual.address.country = dataLocaleMultilingual.country;
  }
  else if (primaryLocaleMultilingual?.addressLine1){
    multilingual.address.addressLine1 = primaryLocaleMultilingual.addressLine1;
    multilingual.address.addressLine2 = primaryLocaleMultilingual.addressLine2;
    multilingual.address.city = primaryLocaleMultilingual.city;
    multilingual.address.stateProvince = primaryLocaleMultilingual.stateProvince;
    multilingual.address.postcode = primaryLocaleMultilingual.postcode;
    multilingual.address.countryCode = primaryLocaleMultilingual.countryCode;
    multilingual.address.country = primaryLocaleMultilingual.country;
  }

  multilingual.name = dataLocaleMultilingual?.displayName || primaryLocaleMultilingual?.displayName;
  multilingual.sortAlias = dataLocaleMultilingual?.sortAlias || primaryLocaleMultilingual?.sortAlias;
  multilingual.showObjective = dataLocaleMultilingual?.showObjective || primaryLocaleMultilingual?.showObjective;
  multilingual.description = dataLocaleMultilingual?.description || primaryLocaleMultilingual?.description;
  multilingual.coverImage = dataLocaleMultilingual?.coverImageUrl || primaryLocaleMultilingual?.coverImageUrl;
  multilingual.logo = dataLocaleMultilingual?.logoUrl || primaryLocaleMultilingual?.logoUrl;

  multilingual.representedBrands = getRepresentedBrandsForDetails(dataLocaleMultilingual, primaryLocaleMultilingual);
  multilingual.representedBrandsAsString = multilingual.representedBrands.map(brands => brands.name).join(joinDelimiter);
  multilingual.stands = profile.stands.map(stand => stand.name).join(joinDelimiter);
  return multilingual;
};

const findByLocale = (multilingual, locale) => {
  if (locale == null) {
    return null;
  }
  const lowerCaseLocale = locale.toLowerCase();
  return find(multilingual, o => o.locale === lowerCaseLocale);
};

const fromResponse = (profileResponse, filterGroup, locale, primaryLocale) => {
  let primaryLocaleMultilingual = null;
  if (primaryLocale) {
    primaryLocaleMultilingual = findByLocale(profileResponse.multilingual, primaryLocale);
  }
  const dataLocaleMultilingual = findByLocale(profileResponse.multilingual, locale);
  const strippedProfile = omit(profileResponse, 'multilingual', 'stands');
  const multilingual = constructMultilingual(profileResponse, dataLocaleMultilingual, primaryLocaleMultilingual);
  const { stands, representedBrandsAsString } = multilingual;
  const strippedMultilingual = omit(multilingual, 'stands', 'representedBrandsAsString');
  const result = Object.assign(
    {},
    strippedProfile,
    { standReference: stands },
    { representedBrandsAsString },
    strippedMultilingual,
    {
      productsAndServices: filterGroup == null ? [] : getFilterItems(strippedProfile.productsAndServices, filterGroup.items)
    },
    { locale },
    { profileQuestions: strippedProfile.profileQuestions || [] },
    { filterCategories: strippedProfile.filterCategories || [] }
  );
  return Object.assign({}, result);
};

const fromListResponse = (profileResponse, filterGroup, locale) => {
  const strippedProfile = omit(profileResponse, 'multilingual', 'stands');
  const joinedStands = profileResponse.stands.map(stand => stand.name).join(joinDelimiter);

  const multilingualForLocale = locale
    ? profileResponse.multilingual.find(x => x.locale.toLowerCase() === locale.toLowerCase())
    : profileResponse.multilingual[0];
  const joinedRepresentedBrands =
    multilingualForLocale && multilingualForLocale.representedBrands ? multilingualForLocale.representedBrands.join(joinDelimiter) : '';

  const multilingual = Object.assign({}, multilingualForLocale, { address: (multilingualForLocale && multilingualForLocale.address) || {} });

  return Object.assign(
    {},
    strippedProfile,
    { standReference: joinedStands },
    multilingual,
    {
      productsAndServices: filterGroup == null ? [] : getFilterItems(strippedProfile.productsAndServices, filterGroup.items)
    },
    { representedBrandsAsString: joinedRepresentedBrands },
    { locale },
    { profileQuestions: strippedProfile.profileQuestions || [] },
    { filterCategories: strippedProfile.filterCategories || [] }
  );
};

const fromDetailsResponse = (profileResponse, filterGroup, locale, primaryLocale) => {
  let primaryLocaleMultilingual = null;
  if (primaryLocale) {
    primaryLocaleMultilingual = findByLocale(profileResponse.multilingual, primaryLocale);
  }
  const dataLocaleMultilingual = findByLocale(profileResponse.multilingual, locale);
  const strippedProfile = omit(profileResponse, 'multilingual', 'stands');
  const multilingual = constructMultilingualForDetails(profileResponse, dataLocaleMultilingual, primaryLocaleMultilingual);
  const { stands, representedBrandsAsString } = multilingual;
  const strippedMultilingual = omit(multilingual, 'stands', 'representedBrandsAsString');
  const result = Object.assign(
    {},
    strippedProfile,
    { standReference: stands },
    { representedBrandsAsString },
    strippedMultilingual,
    {
      productsAndServices: filterGroup == null ? [] : getFilterItemsForDetails(strippedProfile.productsAndServices, filterGroup.items)
    },
    { locale },
    { profileQuestions: strippedProfile.profileQuestions || [] },
    { filterCategories: strippedProfile.filterCategories || [] },
      {email : profileResponse.contactEmail}
  );
  return Object.assign({}, result);
};

const findDataForLocale = (multilingualText, interfaceLocale, defaultLocale) => {
  const availableLocales = multilingualText.map(attr => attr.locale.toLowerCase());
  if (availableLocales.includes(interfaceLocale.toLowerCase())) {
    return multilingualText.find(attr => attr.locale.toLowerCase() === interfaceLocale.toLowerCase());
  }
  return multilingualText.find(attr => attr.locale.toLowerCase() === defaultLocale.toLowerCase());
};

const fromBasicDetails = (profileResponse, interfaceLocale, primaryLocale) => {
  const strippedProfile = omit(profileResponse, 'multilingual');
  const mulitingual = findDataForLocale(profileResponse.multilingual, interfaceLocale, primaryLocale);
  return Object.assign({}, strippedProfile, mulitingual);
};

const trim = (profileFromClient) => {
  const trimmedStands = valuesAsArray(profileFromClient.standReference).join(joinDelimiter);

  return {
    ...profileFromClient,
    standReference: trimmedStands,
    name: profileFromClient.name.trim(),
    companyName: profileFromClient.companyName.trim(),
    sortAlias: profileFromClient.sortAlias ? profileFromClient.sortAlias.trim() : profileFromClient.sortAlias
  };
};

export default {
  addExhibitingOrganisationMutationModel,
  addMainStandHolderMutationModel,
  privateRequestModel,
  protectedRequestModel,
  sharerRequestModel,
  fromResponse,
  fromListResponse,
  fromDetailsResponse,
  trim,
  findByLocale,
  fromBasicDetails
};
